import { Router } from "@angular/router";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import * as moment from "moment-timezone";
import { Observable } from "rxjs";
import { PaykuService } from "src/app/services/payku.service";
import { TokenStorageService } from "src/app/services/token-storage.service";
import { AbonosPanelEnt, MonthlyTotalEnt, DailySummaryEnt } from "src/app/models/Payku/AbonosPanelEnt";
import { PaykuTransactionEnt } from "src/app/models/Payku/PaykuTransactionEnt";
import { Page } from "src/app/shared/models/Page";
import { PagedResponse } from "src/app/shared/models/PagedResponse";
import { SortType } from "@swimlane/ngx-datatable";
import { ExportModalComponent } from "../../layout/export-modal/export-modal.component";
import { AfterViewInit, ChangeDetectorRef, Component, OnInit, TemplateRef, ViewChild } from "@angular/core";
import Swal from "sweetalert2";
import { PermissionService } from "src/app/services/permission.service";
import { BancoEnt, TipoCuenta, CuentaActual, CuentaForm } from "src/app/models/Payku/Banco";
import { LocalStorageService } from "src/app/services/local-storage.service";

type ViewKey = "panel-abonos" | "administrador" | "detalle-transacciones";
type DailyRole = "prev2" | "prev1" | "today" | "next1";
type DailyState = "empty" | "liquidated" | "pending" | "mixed";

interface DailyCardUI {
	label: string;
	date: string;
	dateKey: string;
    gross: string;
    estimated: string;
    reported: string;
	incomplete?: number;
	estado: DailyState;
	role: DailyRole;
}

@Component({
	selector: "app-mantenedor-payku",
	templateUrl: "./mantenedor-payku.component.html",
	styleUrls: ["./mantenedor-payku.component.scss"],
})
export class MantenedorPaykuComponent implements OnInit {
	SortType = SortType;
	@ViewChild("modalCuenta") modalCuenta!: TemplateRef<any>;
	bancos: BancoEnt[] = [];
	tiposCuenta: TipoCuenta[] = [];

	cuentaActual: CuentaActual = {};
	cuentaForm: CuentaForm = {
		sbif: "",
		tipoCuentaId: null,
		numCuenta: "",
		rut: "",
	};
	// Estado UI
	loading = false;
	abonosDataLoaded = false; // Bandera para controlar si ya se cargaron los datos de abonos
	hasActiveAccount = false;
	activeView: "administrador" | "panel-abonos" | "detalle-transacciones" = "administrador";

	// Vista de detalle de transacciones
	detailDate = "";
	detailTransactions: PaykuTransactionEnt[] = [];
	transactionLoading = false;
	modalLoading = false;
	transactionsPage = new Page();
	componentStarted = false;
	isMonthlyView = false; // Para determinar si es vista mensual o diaria

	// Integración/empresa
	hasReseller: boolean = this.tokenService.userHasReseller();
	fechaInicioAdicionalPayku: moment.Moment;
	private emisorId: string = "";
	config = {
		termsAccepted: false,
		paykuHabilitado: false,
        puedeRecaudar: false,
		correoNotificacion: "" as string | null,
	};
	correoNotificacion = "";
	tiposDteDisponibles = [
		{ id: 33, nombre: "Factura" },
		{ id: 34, nombre: "Factura exenta" },
		{ id: 39, nombre: "Boleta" },
		{ id: 41, nombre: "Boleta exenta" },
	];
	tiposSeleccionados = new Set<number>();
	// Buscador (YYYY-MM-DD)
	currentSearchDate: string = moment.tz("America/Santiago").format("YYYY-MM-DD");
	// Fecha mínima permitida para búsqueda (YYYY-MM-DD) — derivada de fechaInicioAdicionalPayku
	minSearchDate: string = "";

	// Configuración para modal de exportación (siguiendo patrón DTE emitidos)
	locale = {
		daysOfWeek: moment.weekdaysMin(),
		monthNames: moment.monthsShort(),
		firstDay: moment.localeData().firstDayOfWeek(),
		applyLabel: "ok",
		clearLabel: "Limpiar",
		format: "DD/MM/YYYY",
	};

	// Datos para UI (sin datos de prueba)
	dailyCards: DailyCardUI[] = [];
	previousMonth = { month: "", gross: "0", estimated: "0", reported: "0", incomplete: 0 };
	currentMonth = { month: "", gross: "0", estimated: "0", reported: "0", incomplete: 0 };
	lastSixMonths = { period: "", chartData: [] as { month: string; value: number }[] };

	// Escala del gráfico
	private lastSixMonthsMax = 1;

	constructor(private paykuService: PaykuService, private tokenService: TokenStorageService, private ref: ChangeDetectorRef, private router: Router, private modalService: NgbModal, public permissions: PermissionService, private localStorage: LocalStorageService) {}

	ngOnInit(): void {
		this.initializeComponent();
		this.initializeTransactionsPage();
	}
	private initializeTransactionsPage(): void {
		this.transactionsPage.pageNumber = 0;
		this.transactionsPage.pageSize = 10;
		this.transactionsPage.totalElements = 0;
		this.transactionsPage.totalPages = 0;
		this.componentStarted = true;
	}

	/* -------------------- Inicialización -------------------- */

	private initializeComponent(): void {
		const emisorId = this.tokenService.getCompany();
		if (!emisorId) {
			this.hasActiveAccount = false;
			return;
		}
		const adicionales = this.localStorage.getAdicionales() || [];
		const adicionalPayku = adicionales.find((x: any) => x.idAdicional === 5);
		if (adicionalPayku && adicionalPayku.fechaInicio) {
			this.fechaInicioAdicionalPayku = moment.tz(adicionalPayku.fechaInicio, "America/Santiago");
			this.minSearchDate = this.fechaInicioAdicionalPayku.format("YYYY-MM-DD");
			const todayStr = moment.tz("America/Santiago").format("YYYY-MM-DD");
			if (this.currentSearchDate < this.minSearchDate) {
				this.currentSearchDate = this.minSearchDate;
			} else if (todayStr < this.minSearchDate) {
				this.currentSearchDate = this.minSearchDate;
			}
		} else {
			this.minSearchDate = "";
		}
		this.emisorId = emisorId;
		if (this.emisorId) this.cargarConfiguracionAdmin();
		this.checkActiveAccount();
	}
	private cargarConfiguracionAdmin(): void {
		this.loading = true;
		this.paykuService.obtenerConfig(this.emisorId).subscribe({
			next: (cfg) => {
				this.config.termsAccepted = !!cfg.termsAccepted;
				this.config.paykuHabilitado = !!cfg.paykuHabilitado?.habilitado;
                this.config.puedeRecaudar = !!cfg.paykuHabilitado?.puedeRecaudar;
				this.config.correoNotificacion = cfg.correoNotificacion || "";
				this.correoNotificacion = this.config.correoNotificacion || "";

				this.tiposSeleccionados.clear();
				(cfg.tiposDteActivos || []).forEach((t: number) => this.tiposSeleccionados.add(t));

				this.loading = false;
				this.ref.detectChanges();
			},
			error: (e) => {
				console.error("Error obteniendo config Payku:", e);
				this.loading = false;
				this.ref.detectChanges();
			},
		});
	}

	cambiarEstadoPayku(habilitar: boolean): void {
		if (this.loading || this.config.paykuHabilitado === habilitar) return;

		this.loading = true;
		this.ref.detectChanges();

		this.paykuService.activaDesactivarPayku(this.emisorId, habilitar).subscribe({
			next: (res) => {
				this.config.paykuHabilitado = habilitar;
                this.cargarConfiguracionAdmin();
				this.loading = false;
				this.ref.detectChanges();

				if (habilitar) {
					Swal.fire({ icon: "success", title: "Payku habilitado", text: res?.message || "Payku habilitado." });
				} else {
					Swal.fire({ icon: "info", title: "Payku deshabilitado", text: "No se generarán nuevos links/QR ni se iniciarán nuevos pagos desde enlaces existentes. Los pagos ya iniciados seguirán siendo conciliados." });
				}
			},
			error: (e) => {
				this.loading = false;
				this.ref.detectChanges();
				Swal.fire({ icon: "error", title: "Error", text: e?.error?.message || "No se pudo cambiar el estado" });
			},
		});
	}

	onCheckTipoDte(id: number, ev: any): void {
		if (ev.target.checked) this.tiposSeleccionados.add(id);
		else this.tiposSeleccionados.delete(id);
	}

	guardarTiposDte(): void {
		const tipos = Array.from(this.tiposSeleccionados);
		if (!tipos.length) {
			Swal.fire({
				icon: "warning",
				title: "Selección de tipos de documento",
				text: "Debes seleccionar al menos un tipo de documento para usar Payku.",
			});
			return;
		}

		this.loading = true;
		this.ref.detectChanges();

		this.paykuService.actualizarTiposDte(this.emisorId, tipos).subscribe({
			next: (res) => {
				this.loading = false;
				this.ref.detectChanges();
				Swal.fire({ icon: "success", title: "Tipos DTE actualizados", text: res?.message || "Tipos DTE actualizados" });
			},
			error: (e) => {
				this.loading = false;
				this.ref.detectChanges();
				Swal.fire({ icon: "error", title: "Error al actualizar tipos DTE", text: e?.error?.message || "Error al actualizar tipos DTE" });
			},
		});
	}

	guardarCorreo(): void {
		const correo = this.correoNotificacion?.trim();
		if (!correo) {
			Swal.fire({ icon: "warning", title: "Correo inválido", text: "Debes ingresar un correo válido." });
			return;
		}

		this.loading = true;
		this.ref.detectChanges();

		this.paykuService.actualizarCorreoNotificacion(this.emisorId, correo).subscribe({
			next: (res) => {
				this.loading = false;
				this.ref.detectChanges();
				Swal.fire({ icon: "success", title: "Correo de notificaciones actualizado", text: res?.message || "Correo de notificaciones actualizado" });
			},
			error: (e) => {
				this.loading = false;
				this.ref.detectChanges();
				Swal.fire({ icon: "error", title: "Error al actualizar correo", text: e?.error?.message || "Error al actualizar correo" });
			},
		});
	}

	editarDatosBancarios(): void {
		if (!(this.permissions.getPermission("83-24").asignado)) { return; }
		this.modalLoading = true;

		Promise.all([this.paykuService.getBancos().toPromise(), this.paykuService.getTiposCuenta().toPromise(), this.paykuService.getCuentaBancaria(this.emisorId).toPromise()])
			.then(([bancos = [], tipos = [], resp]) => {
				this.bancos = bancos || [];
				this.tiposCuenta = tipos || [];

				const data = resp?.data;
				this.cuentaActual = data || {};

				this.cuentaForm.sbif = data?.sbif || (this.bancos[0]?.sbif ?? "");
				this.cuentaForm.tipoCuentaId = data?.tipoCuentaId ?? this.tiposCuenta[0]?.tipoCuentaId ?? null;
				this.cuentaForm.numCuenta = "";
				this.cuentaForm.rut = data?.rut || "";

				this.modalLoading = false;
				this.ref.detectChanges();
				this.modalService.open(this.modalCuenta, { size: "lg", backdrop: "static", keyboard: false });
			})
			.catch((err) => {
				this.modalLoading = false;
				this.ref.detectChanges();
				Swal.fire({ icon: "error", title: "Error", text: "No se pudo abrir la edición bancaria" });
			});
	}

	guardarCuentaBancaria(modalRef: any): void {
		if (!(this.permissions.getPermission("83-24").asignado)) { return; }
		if (!this.cuentaForm.sbif || !this.cuentaForm.tipoCuentaId || !this.cuentaForm.numCuenta || !this.cuentaForm.rut) {
			Swal.fire({ icon: "warning", title: "Completa los datos", text: "Banco, tipo, número y RUT son obligatorios." });
			return;
		}

		this.modalLoading = true;
		this.ref.detectChanges();

		this.paykuService
			.upsertCuentaBancaria({
				emisorId: this.emisorId,
				sbif: this.cuentaForm.sbif,
				tipoCuentaId: this.cuentaForm.tipoCuentaId!,
				numCuenta: this.cuentaForm.numCuenta.trim(),
				rut: this.cuentaForm.rut.trim(),
			})
			.subscribe({
				next: (res) => {
					this.modalLoading = false;
					this.ref.detectChanges();
					Swal.fire({ icon: "success", title: "Guardado", text: res?.message || "Datos bancarios guardados" });
					modalRef.close();
					this.cargarConfiguracionAdmin();
				},
				error: (e) => {
					this.modalLoading = false;
					this.ref.detectChanges();
					Swal.fire({ icon: "error", title: "Error", text: e?.error?.message || "No se pudo guardar" });
				},
			});
	}
	private checkActiveAccount(): void {
		this.loading = true;
		this.paykuService.hasActiveAccount(this.emisorId).subscribe({
			next: (resp: any) => {
				this.hasActiveAccount = !!resp?.data;
				// Solo terminar el loading inicial, no cargar datos de abonos aún
				this.loading = false;
				this.ref.detectChanges();
			},
			error: () => {
				this.hasActiveAccount = false;
				this.loading = false;
				this.ref.detectChanges();
			},
		});
	}

	/* -------------------- Carga de datos -------------------- */

	loadAbonosByDate(date: string): void {
		if (!this.emisorId || !date) {
			this.loading = false;
			return;
		}

		this.loading = true;
		this.currentSearchDate = date;

		this.paykuService.getAbonosByDate(this.emisorId, date).subscribe({
			next: (response: any) => {
				try {
					const data: AbonosPanelEnt = response?.data;
					this.updatePanelData(data, date);
					this.abonosDataLoaded = true; // Marcar que los datos se han cargado
				} finally {
					this.loading = false;
					this.ref.detectChanges();
				}
			},
			error: () => {
				this.loading = false;
				this.ref.detectChanges();
			},
		});
	}

	private updatePanelData(data: AbonosPanelEnt, searchDate: string): void {
		// Helpers
		const [yy, mm, dd] = searchDate.split("-").map(Number);
		const base = new Date(yy, mm - 1, dd);

		const fmtDate = (d: Date) => d.toLocaleDateString("es-CL", { day: "2-digit", month: "short", year: "numeric" });

		const dayName = (d: Date) => {
			const n = d.toLocaleDateString("es-CL", { weekday: "long" });
			return n.charAt(0).toUpperCase() + n.slice(1);
		};

		const toDate = (iso: string) => {
			const [y, m, d] = iso.slice(0, 10).split("-").map(Number);
			return new Date(y, m - 1, d);
		};

		// 4 tarjetas desde dailyStrip (orden fijo)
		const order: Record<DailyRole, number> = { prev2: 0, prev1: 1, today: 2, next1: 3 };
		const strip = (data.dailyStrip || []).slice().sort((a: DailySummaryEnt, b: DailySummaryEnt) => order[a.role] - order[b.role]);

		this.dailyCards = strip.map((it: DailySummaryEnt) => {
			const d = toDate(it.date as any);
			const dateKey = String(it.date).slice(0, 10);
			return {
				label: dateKey === moment.tz("America/Santiago").format("YYYY-MM-DD") ? "Hoy" : dayName(d),
				date: fmtDate(d),
				dateKey,
                gross: this.formatCLP(it.brutoCobrado || 0),
                estimated: this.formatCLP(it.netoEstimado || 0),
                reported: this.formatCLP(it.netoInformado || 0),
				incomplete: it.liquidacionesSinImporte || 0,
				estado: it.estado,
				role: it.role,
			};
		});

		const summaries = data.ultimos6Meses || [];
		const previousBase = new Date(base.getFullYear(), base.getMonth() - 1, 1);
		const currentSummary = summaries.find((m) => m.year === base.getFullYear() && m.month === base.getMonth() + 1);
		const previousSummary = summaries.find((m) => m.year === previousBase.getFullYear() && m.month === previousBase.getMonth() + 1);

		// Tarjetas mensuales -> usar directamente los totales
		const monthName = (d: Date) => d.toLocaleDateString("es-CL", { month: "long", year: "numeric" });

		this.currentMonth = {
			month: monthName(new Date(base.getFullYear(), base.getMonth(), 1)),
            gross: this.formatCLP(currentSummary?.brutoCobrado || 0),
            estimated: this.formatCLP(currentSummary?.netoEstimado || 0),
            reported: this.formatCLP(currentSummary?.netoInformado || 0),
            incomplete: currentSummary?.liquidacionesSinImporte || 0,
		};
		this.previousMonth = {
			month: monthName(new Date(base.getFullYear(), base.getMonth() - 1, 1)),
            gross: this.formatCLP(previousSummary?.brutoCobrado || 0),
            estimated: this.formatCLP(previousSummary?.netoEstimado || 0),
            reported: this.formatCLP(previousSummary?.netoInformado || 0),
            incomplete: previousSummary?.liquidacionesSinImporte || 0,
		};

		// Gráfico (últimos 6 meses)
		const chart = (data.ultimos6Meses || []).map((m: MonthlyTotalEnt) => {
			const d = new Date(m.year, m.month - 1, 1);
			const label = `${d.toLocaleDateString("es-CL", { month: "short" }).toUpperCase()} ${m.year}`;
			return { month: m.liquidacionesSinImporte ? `${label} (incompleto)` : label, value: m.total || 0 };
		});

		this.lastSixMonthsMax = Math.max(...chart.map((c) => c.value), 1);
		this.lastSixMonths = {
			period: data.periodoUltimos6Meses || "",
			chartData: chart,
		};
	}

	/* -------------------- Utilidades UI -------------------- */

	private formatCLP(value: number): string {
		try {
			return new Intl.NumberFormat("es-CL").format(value || 0);
		} catch {
			return String(value || 0);
		}
	}

	formatCLPForTooltip(value: number): string {
		return this.formatCLP(value);
	}

	getBarHeight(value: number): number {
		if (!value || value === 0) return 5; // Altura mínima para barras vacías
		if (!this.lastSixMonthsMax || this.lastSixMonthsMax === 0) return 5;

		const percentage = Math.round((value / this.lastSixMonthsMax) * 100);
		return Math.max(10, Math.min(100, percentage)); // Mínimo 10% para valores > 0
	}

	setActiveView(view: ViewKey): void {
		this.activeView = view;

		// Si cambia a panel-abonos y no se han cargado los datos aún, cargarlos
		if (view === "panel-abonos" && !this.abonosDataLoaded && this.hasActiveAccount) {
			this.loadAbonosByDate(this.currentSearchDate);
		}
	}

	searchAbonos(date: string): void {
		if (!date) {
			this.currentSearchDate = moment.tz("America/Santiago").format("YYYY-MM-DD");
			this.loadAbonosByDate(this.currentSearchDate);
			return;
		}

		// Si existe una fecha mínima, bloquear búsquedas anteriores
		if (this.minSearchDate && date < this.minSearchDate) {
			Swal.fire({ icon: 'warning', title: 'Fecha no permitida', text: `No puedes buscar antes de ${this.minSearchDate}.` });
			// Forzar el input a la fecha mínima
			this.currentSearchDate = this.minSearchDate;
			return;
		}

		this.loadAbonosByDate(date);
	}

	/* -------------------- Exportación masiva -------------------- */

	downloadMassiveExtraction(): void {
		const modalRef = this.modalService.open(ExportModalComponent, {
			size: "md",
			centered: true,
		});

		modalRef.componentInstance.locale = this.locale;
		modalRef.componentInstance.type = 999; // Tipo especial para PayKu
		modalRef.componentInstance.filter = {
			emisorId: this.emisorId,
			fechaDesde: null,
			fechaHasta: null,
		};
		modalRef.componentInstance.getDataFunction = this.getTransactionsToExport.bind(this);
		modalRef.componentInstance.exportData.subscribe((isLoading) => {
			this.loading = isLoading;
			this.ref.detectChanges();
		});
	}

	getTransactionsToExport(filter: any): Observable<PaykuTransactionEnt[]> {
		return this.paykuService.exportTransactionsMassive(filter.emisorId, filter.fechaDesde, filter.fechaHasta);
	}

	/* -------------------- Vista de detalle de transacciones -------------------- */

	viewDayDetail(card: DailyCardUI): void {
		this.navigateToTransactionDetail(card.dateKey, false); // Fecha chilena calculada por el backend.
	}

	viewMonthDetail(isCurrentMonth: boolean): void {
		const targetDate = moment.tz(this.currentSearchDate, "YYYY-MM-DD", "America/Santiago").startOf("month");
		if (!isCurrentMonth) targetDate.subtract(1, "month");
		const dateStr = targetDate.format("YYYY-MM-DD");
		this.navigateToTransactionDetail(dateStr, true); // true = vista mensual
	}

	private navigateToTransactionDetail(date: string, monthly: boolean = false): void {
		this.detailDate = date;
		this.isMonthlyView = monthly;
		this.activeView = "detalle-transacciones";
		this.resetTransactionsPage();
		this.loadTransactionsByDate();
	}

	loadTransactionsByDate(): void {
		if (!this.emisorId || !this.detailDate) {
			return;
		}

		this.transactionLoading = true;
		const pageNumber = this.transactionsPage.pageNumber + 1; // Incrementar para backend (1-indexed)
		const pageSize = this.transactionsPage.pageSize;

		this.paykuService.getTransactionsByDate(this.emisorId, this.detailDate, pageNumber, pageSize, this.isMonthlyView).subscribe({
			next: (response: any) => {
				if (response?.data) {
					this.detailTransactions = response.data; // Los datos están directamente en response.data
					this.setTransactionsPageFromResponse(response); // Pasar la respuesta completa
				} else {
					this.detailTransactions = [];
				}
				this.transactionLoading = false;
				this.ref.detectChanges();
			},
			error: () => {
				this.detailTransactions = [];
				this.transactionLoading = false;
				this.ref.detectChanges();
			},
		});
	}

	private setTransactionsPageFromResponse(data: any): void {
		this.transactionsPage.pageNumber = data.pageNumber - 1 < 0 ? 0 : data.pageNumber - 1; // Convertir de 1-indexed a 0-indexed
		this.transactionsPage.pageSize = data.pageSize || 10;
		this.transactionsPage.totalElements = data.totalRecords || 0;
		this.transactionsPage.totalPages = data.totalPages || 0;
	}

	private resetTransactionsPage(): void {
		this.transactionsPage.pageNumber = 0;
		this.detailTransactions = [];
	}

	setTransactionPage(pageInfo: any): void {
		this.transactionsPage.pageNumber = pageInfo.offset;
		this.loadTransactionsByDate();
	}

	backToPanelAbonos(): void {
		this.activeView = "panel-abonos";
		this.detailTransactions = [];
		this.detailDate = "";
		this.isMonthlyView = false; // Resetear el flag
		this.resetTransactionsPage();
	}

	onTransactionDateChange(): void {
		this.resetTransactionsPage();
		this.loadTransactionsByDate();
	}

	getEstadoLabel(estado: string): string {
		switch (estado?.toLowerCase()) {
			case "liquidated":
			case "completado":
				return "Liquidado";
			case "pending":
			case "pendiente":
				return "Pendiente";
			case "failed":
			case "fallido":
				return "Fallido";
			default:
				return estado || "Desconocido";
		}
	}

	formatCLPForDisplay(value: number): string {
		return this.formatCLP(value);
	}

	/* -------------------- Navegación a DTE -------------------- */

	navigateToDte(transaction: PaykuTransactionEnt): void {
		if (transaction.dteFolio) {
			// Navegar a documentos emitidos con filtro de folio y emisorId siguiendo el patrón del proyecto
			this.router.navigateByUrl("ventas/documentos-emitidos", {
				state: {
					folio: transaction.dteFolio.toString(),
					filterEmisor: this.emisorId,
                    filterSucursal: transaction.sucursalId,
                    codigoSii: transaction.tipo,
                    dteId: transaction.dteId,
					filterDescription: `Filtrado por folio ${transaction.dteFolio} desde Payku`,
				},
			});
		}
	}
}
